import {
  CHIP_SETS,
  CHIP_SET_IDS,
  DECKS,
  DECK_IDS,
  FELTS,
  FELT_IDS,
  readTheme,
  type ChipSetId,
  type DeckId,
  type FeltId,
} from '@tv-casino/shared';
import type { CasinoLookup } from '@tv-casino/shared';

/**
 * The pure view-model behind the phone's casino controls — the owner's
 * counterpart to the host panel. No React, no fetching: the route feeds it
 * the lineup lookup it already holds and renders what comes back.
 */

/** One house felt the owner can dress the venue in, from their phone. */
export interface CasinoOwnerFeltOption {
  id: FeltId;
  label: string;
  active: boolean;
}

/** One house chip set the owner can put on the venue's rails. */
export interface CasinoOwnerChipSetOption {
  id: ChipSetId;
  label: string;
  active: boolean;
}

/** One house deck the owner can deal every table from. */
export interface CasinoOwnerDeckOption {
  id: DeckId;
  label: string;
  active: boolean;
}

export interface CasinoOwnerPanelVM {
  casinoName: string;
  joinCode: string;
  /** Every house felt, in catalog order, the venue's own one marked active. */
  felts: CasinoOwnerFeltOption[];
  /** Every house chip set, in catalog order, the venue's own one marked active. */
  chipSets: CasinoOwnerChipSetOption[];
  /** Every house deck, in catalog order, the venue's own one marked active. */
  decks: CasinoOwnerDeckOption[];
}

/**
 * The owner's theme pickers from the lookup Play already reads for the lobby.
 * Null before the venue resolves, so the panel renders nothing rather than a
 * frame of dead swatches.
 */
export function casinoOwnerPanelModel(casino: CasinoLookup | null): CasinoOwnerPanelVM | null {
  if (!casino) return null;

  const { felt, chips, deck } = readTheme(casino.theme);

  return {
    casinoName: casino.name,
    joinCode: casino.joinCode,
    felts: FELT_IDS.map((id) => ({ id, label: FELTS[id].label, active: id === felt })),
    chipSets: CHIP_SET_IDS.map((id) => ({ id, label: CHIP_SETS[id].label, active: id === chips })),
    decks: DECK_IDS.map((id) => ({ id, label: DECKS[id].label, active: id === deck })),
  };
}
